import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { AccessRoleEnum } from '../models/framework.model';
import { AuthService } from '../services/auth.service';
import { DealerStateService } from '../services/dealer-state.service';

/**
 * Blocks Home (`/dashboard` list) for STE pricing-group dealers after dealer context is loaded.
 * Does not block `/dashboard/:appId` — {@link AppRedirectComponent} resolves SSO routing (AOS-5280).
 * Never returns a pending Observable (header loads only after navigation completes).
 */
@Injectable({
  providedIn: 'root'
})
export class SfsSteHomeGuard {
  private authService = inject(AuthService);
  private router = inject(Router);
  private dealerState = inject(DealerStateService);

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean | UrlTree {
    if (route.data['accessRole'] !== AccessRoleEnum.Home) {
      return true;
    }

    const path = state.url.split('?')[0];
    if (/^\/dashboard\/[^/]+/.test(path)) {
      return true;
    }

    const dealerno = this.authService.getUser()?.dealerno;
    if (!dealerno) {
      return this.evaluateHomeAccess();
    }

    if (!this.dealerState.dealerNavigationContextReady()) {
      return true;
    }

    return this.evaluateHomeAccess();
  }

  private evaluateHomeAccess(): boolean | UrlTree {
    if (this.dealerState.isSfsPricingGroup()) {
      return this.router.createUrlTree(['/rehash-sfs']);
    }
    return true;
  }
}
